import Select from 'react-select';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Loading from '@components/Shared/Loader/Loading';
import { Card, CardFooter, CardHeader } from '@components/card/card';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { initReport, getReportPagedData, selectReport, setReportRequest } from '@store/DynamicSlice/reportTableSlice';
import ReportExportExcelModal, { type ExportColumn } from '@Helper/ReportPaged/ReportExportExcelModal';
import ReportPagination from '@Helper/ReportPaged/ReportPagination';
import useGlChartLookups from '@hooks/Finance/ChartAccountsDetails/useGlChartLookups';
import useGetSuppliersList from '@hooks/NTX/Peoples/useGetSuppliersList';
import useGetInventoryWithPermission from '@hooks/NTX/Organization/useGetInventoryWithPermission';
import useGetBankAccountsList from '@hooks/Finance/BankAccounts/useGetBankAccountsList';

/** =========================
 * INNER REQUEST SHAPE
 * ========================= */
type AnalyticPartyStatementRequestDto = {
  pageIndex?: number;
  pageSize?: number;
  fromDate?: string | null;
  toDate?: string | null;
  branchId?: string | null;
  analyticPartyId?: string | null;
  analyticPartyType?: string | null;
};

/** =========================
 * RESPONSE SHAPE (Array items)
 * ========================= */
type RowDto = {
  jE_HEADER_ID: string | null;
  gL_DATE: string | null;
  name: string | null;
  namE2: string | null;
  brancH_NAME: string | null;
  analytiC_PARTY_ID: string | null;
  analytiC_PARTY_TYPE: string | null;
  entereD_DR: number;
  entereD_CR: number;
  cumulativE_BALANCE: number;
};

const REPORT_KEY = 'RPT_AnalyticPartyStatement';
const ENDPOINT = 'FinReports/GetAnalyticPartyStatementPaged';

const safe = (v: any, fallback = '—') => (v === null || v === undefined || v === '' ? fallback : String(v));

const formatNumber = (num: number, digits: number = 2) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  }).format(num || 0);
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
};

const getOptId = (opt: any): string | null => {
  const v = opt?.value ?? opt?.id ?? opt?.ID ?? opt?.key ?? null;
  return v ? String(v) : null;
};

const isoOrNull = (d: any) => {
  if (!d) return null;
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) return `${d}T00:00:00.000Z`;
  if (d instanceof Date) return d.toISOString();
  return null;
};

const cleanNulls = <T extends Record<string, any>>(obj: T): Partial<T> => {
  const out: any = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === null || v === undefined || v === '') continue;
    out[k] = v;
  }
  return out;
};

const AnalyticPartyStatement_Paged = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const isAr = i18n.language !== 'en';

  // options
  const { OptionsBranches, loading: loadingBranches } = useGlChartLookups();

  // TODO: استبدل هذه المتغيرات بالـ Hooks الخاصة بك لجلب كل نوع
  const { options: customersOptions = [], loading: loadingCustomers = false } = { options: [], loading: false }; // useCustomersHook()
  //   const { options: suppliersOptions = [], loading: loadingSuppliers = false } = { options: [], loading: false }; // useSuppliersHook()
  //   const { options: inventoryOptions = [], loading: loadingInventory = false } = { options: [], loading: false }; // useInventoryHook()
  //   const { options: bankOptions = [], loading: loadingBanks = false } = { options: [], loading: false }; // useBankAccountsHook()
  const { options: personOptions = [], loading: loadingPersons = false } = { options: [], loading: false }; // usePersonsHook()

  const { OptionSuppliers } = useGetSuppliersList();
  const { InventoryOptions } = useGetInventoryWithPermission();
  const { OptionsBankAccounts } = useGetBankAccountsList();

  // Filters state
  const [branchFilter, setBranchFilter] = useState<any>(null);
  const [partyTypeFilter, setPartyTypeFilter] = useState<any>(null);
  const [analyticPartyFilter, setAnalyticPartyFilter] = useState<any>(null);

  // Date range
  const [fromDate, setFromDate] = useState<any>(null);
  const [toDate, setToDate] = useState<any>(null);

  const [showExport, setShowExport] = useState(false);

  // report state
  const report = useAppSelector(s => selectReport(s as any, REPORT_KEY));
  const items = (report?.items ?? []) as RowDto[];
  const meta = report?.meta ?? null;
  const loading = report?.loading ?? false;
  const error = report?.error ?? null;

  // قائمة أنواع الجهات التحليلية المتاحة
  const partyTypeOptions = useMemo(
    () => [
      { value: 'Customers', label: isAr ? 'العملاء' : 'Customers' },
      { value: 'Suppliers', label: isAr ? 'الموردين' : 'Suppliers' },
      { value: 'Inventory', label: isAr ? 'المخازن' : 'Inventory' },
      { value: 'BankAccount', label: isAr ? 'الحسابات البنكية والصناديق' : 'Bank Account' },
      { value: 'Person', label: isAr ? 'الموظفين' : 'Emp' }
    ],
    [isAr]
  );

  // تحديد القائمة وحالة التحميل للجهة التحليلية بناءً على النوع المختار
  const currentPartyOptions = useMemo(() => {
    switch (partyTypeFilter?.value) {
      case 'Customers':
        return customersOptions;
      case 'Suppliers':
        return OptionSuppliers;
      case 'Inventory':
        return InventoryOptions;
      case 'BankAccount':
        return OptionsBankAccounts;
      case 'Person':
        return personOptions;
      default:
        return [];
    }
  }, [partyTypeFilter, customersOptions, personOptions]);

  const currentPartyLoading = useMemo(() => {
    switch (partyTypeFilter?.value) {
      case 'Customers':
        return loadingCustomers;
      //   case 'Suppliers': return loadingSuppliers;
      //   case 'Inventory': return loadingInventory;
      //   case 'BankAccount': return loadingBanks;
      case 'Person':
        return loadingPersons;
      default:
        return false;
    }
  }, [partyTypeFilter, loadingCustomers, loadingPersons]);

  // تفريغ اختيار الجهة التحليلية عند تغيير نوع الجهة
  useEffect(() => {
    setAnalyticPartyFilter(null);
  }, [partyTypeFilter]);

  const activeFiltersCount = useMemo(() => {
    let c = 0;
    if (branchFilter) c++;
    if (partyTypeFilter) c++;
    if (analyticPartyFilter) c++;
    if (fromDate) c++;
    if (toDate) c++;
    return c;
  }, [branchFilter, partyTypeFilter, analyticPartyFilter, fromDate, toDate]);

  const exportColumns: ExportColumn<RowDto>[] = [
    { headerAr: 'تاريخ القيد', headerEn: 'GL Date', value: r => formatDate(r.gL_DATE) },
    { headerAr: 'الفرع', headerEn: 'Branch', value: r => r.brancH_NAME || '' },
    { headerAr: 'نوع الجهة التحليلية', headerEn: 'Party Type', value: r => r.analytiC_PARTY_TYPE || '' },
    { headerAr: 'مدين', headerEn: 'Debit', value: r => r.entereD_DR || 0 },
    { headerAr: 'دائن', headerEn: 'Credit', value: r => r.entereD_CR || 0 },
    { headerAr: 'الرصيد التراكمي', headerEn: 'Cumulative Balance', value: r => r.cumulativE_BALANCE || 0 },
    { headerAr: 'الوصف', headerEn: 'Description', value: r => (isAr ? r.namE2 || r.name : r.name || r.namE2) || '' }
  ];

  useEffect(() => {
    const initialRequest: AnalyticPartyStatementRequestDto = {
      pageIndex: 0,
      pageSize: 50
    };

    dispatch(
      initReport({
        reportKey: REPORT_KEY,
        endpoint: ENDPOINT,
        initialRequest
      })
    );

    dispatch(getReportPagedData({ reportKey: REPORT_KEY }));
  }, [dispatch]);

  const applyFilters = () => {
    const branchId = getOptId(branchFilter);
    const partyId = getOptId(analyticPartyFilter);
    const partyType = partyTypeFilter?.value || null;

    const prevReq = (((report as any)?.request ?? {}) as AnalyticPartyStatementRequestDto) || {};

    const nextReq: AnalyticPartyStatementRequestDto = {
      ...prevReq,
      pageIndex: 0,
      pageSize: prevReq.pageSize ?? 50,
      fromDate: isoOrNull(fromDate),
      toDate: isoOrNull(toDate),
      branchId: branchId,
      analyticPartyId: partyId,
      analyticPartyType: partyType
    };

    dispatch(setReportRequest({ reportKey: REPORT_KEY, request: cleanNulls(nextReq) as any }));
    dispatch(getReportPagedData({ reportKey: REPORT_KEY }));
  };

  const clearFilters = () => {
    setBranchFilter(null);
    setPartyTypeFilter(null);
    setAnalyticPartyFilter(null);
    setFromDate(null);
    setToDate(null);

    const resetReq: AnalyticPartyStatementRequestDto = {
      pageIndex: 0,
      pageSize: 50
    };

    dispatch(setReportRequest({ reportKey: REPORT_KEY, request: resetReq as any }));
    dispatch(getReportPagedData({ reportKey: REPORT_KEY }));
  };

  return (
    <>
      <CardHeader className='mb-3'>
        <div className='row g-2 align-items-center'>
          {/* الفرع */}
          <div className='col-lg-4'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-store text-primary'></i>
              </span>
              <Select
                isLoading={loadingBranches}
                value={branchFilter}
                onChange={setBranchFilter}
                options={OptionsBranches}
                placeholder={isAr ? 'الفرع' : 'Branch'}
                isClearable
                isSearchable
                className='flex-grow-1'
                classNamePrefix='react-select'
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          </div>

          {/* نوع الجهة التحليلية */}
          <div className='col-lg-3'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-tags text-warning'></i>
              </span>
              <Select
                value={partyTypeFilter}
                onChange={setPartyTypeFilter}
                options={partyTypeOptions}
                placeholder={isAr ? 'نوع الجهة' : 'Party Type'}
                isClearable
                className='flex-grow-1'
                classNamePrefix='react-select'
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          </div>

          {/* الجهة التحليلية */}
          <div className='col-lg-5'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-users text-info'></i>
              </span>
              <Select
                isLoading={currentPartyLoading}
                value={analyticPartyFilter}
                onChange={setAnalyticPartyFilter}
                options={currentPartyOptions}
                isDisabled={!partyTypeFilter}
                placeholder={isAr ? 'الجهة التحليلية' : 'Analytic Party'}
                isClearable
                isSearchable
                className='flex-grow-1'
                classNamePrefix='react-select'
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
          </div>
        </div>

        <div className='row mt-2'>
          {/* من تاريخ */}
          <div className='col-lg-3'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-calendar'></i>
              </span>
              <input
                type='date'
                className='form-control'
                value={fromDate || ''}
                onChange={e => setFromDate(e.target.value)}
                placeholder={isAr ? 'من تاريخ' : 'From Date'}
              />
            </div>
          </div>

          {/* إلى تاريخ */}
          <div className='col-lg-3'>
            <div className='input-group'>
              <span className='input-group-text fw-bold'>
                <i className='fas fa-calendar'></i>
              </span>
              <input
                type='date'
                className='form-control'
                value={toDate || ''}
                onChange={e => setToDate(e.target.value)}
                min={fromDate || undefined}
                placeholder={isAr ? 'إلى تاريخ' : 'To Date'}
              />
            </div>
          </div>
          {/* Buttons */}
          <div className='col-6 text-end'>
            <button
              className='btn btn-outline-secondary me-2'
              type='button'
              onClick={clearFilters}
              title={t('ClearFilters')}
              disabled={activeFiltersCount === 0}
            >
              <i className='fas fa-times fa-fw'></i>
            </button>
            <button
              className='btn btn-theme me-2'
              type='button'
              onClick={applyFilters}
              title={t('ApplyFilters')}
              disabled={activeFiltersCount === 0}
            >
              <i className='fas fa-filter fa-fw'></i>
            </button>

            <button
              className='btn btn-outline-success'
              type='button'
              onClick={() => setShowExport(true)}
              title={t('Export')}
              disabled={loading}
            >
              <i className='fas fa-file-excel fa-fw'></i>
            </button>
          </div>
        </div>
      </CardHeader>

      <Card className='mb-4'>
        <Loading loading={loading} error={error}>
          <div className='table-responsive' style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
            <table className='table table-hover mb-0'>
              <thead className='sticky-top' style={{ top: 0, zIndex: 10 }}>
                <tr className='bg-theme bg-opacity-35' style={{ backdropFilter: 'blur(20px)' }}>
                  <th className='small' style={{ minWidth: '110px' }}>
                    <div className='d-flex align-items-center'>{t('Date')}</div>
                  </th>
                  <th className='small' style={{ minWidth: '150px' }}>
                    <div className='d-flex align-items-center'>{isAr ? 'الفرع' : 'Branch'}</div>
                  </th>
                  {/* <th className='small' style={{ minWidth: '120px' }}>
                    <div className='d-flex align-items-center'>{isAr ? 'النوع' : 'Type'}</div>
                  </th> */}
                  <th className='small text-end' style={{ minWidth: '120px' }}>
                    <div className='d-flex align-items-center justify-content-end'>{t('Debit')}</div>
                  </th>
                  <th className='small text-end' style={{ minWidth: '120px' }}>
                    <div className='d-flex align-items-center justify-content-end'>{t('Credit')}</div>
                  </th>
                  <th className='small text-end' style={{ minWidth: '120px' }}>
                    <div className='d-flex align-items-center justify-content-end'>{isAr ? 'الرصيد' : 'Balance'}</div>
                  </th>
                  <th className='small' style={{ minWidth: '350px' }}>
                    <div className='d-flex align-items-center'>{t('Description')}</div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {!loading && items.length > 0 ? (
                  items.map((r, idx) => {
                    const desc = isAr ? r.namE2 || r.name : r.name || r.namE2;
                    // تمييز الرصيد الافتتاحي
                    const isOpening = !r.jE_HEADER_ID && !r.gL_DATE;

                    return (
                      <tr
                        key={`${r.jE_HEADER_ID || 'OB'}-${idx}`}
                        className={`align-middle ${isOpening ? 'table-light fw-bold' : ''}`}
                      >
                        <td>
                          <div className={isOpening ? 'text-primary' : 'fw-medium'}>
                            {r.gL_DATE ? r.gL_DATE.split('T')[0] : isAr ? 'رصيد افتتاحي' : 'Opening'}
                          </div>
                        </td>

                        <td>
                          <div className='fw-medium'>{safe(r.brancH_NAME)}</div>
                        </td>

                        {/* <td>
                          <div className='text-muted small'>{safe(r.analytiC_PARTY_TYPE)}</div>
                        </td> */}

                        <td className='text-end'>
                          {r.entereD_DR > 0 ? (
                            <div className='fw-bold text-success'>{formatNumber(r.entereD_DR)}</div>
                          ) : (
                            <div className='text-muted'>—</div>
                          )}
                        </td>

                        <td className='text-end'>
                          {r.entereD_CR > 0 ? (
                            <div className='fw-bold text-danger'>{formatNumber(r.entereD_CR)}</div>
                          ) : (
                            <div className='text-muted'>—</div>
                          )}
                        </td>

                        <td className='text-end'>
                          <div className={`fw-bold ${r.cumulativE_BALANCE < 0 ? 'text-danger' : 'text-success'}`}>
                            {formatNumber(r.cumulativE_BALANCE)}
                          </div>
                        </td>

                        <td>
                          <div className='d-flex align-items-center'>
                            <span className={desc ? '' : 'text-muted fst-italic'}>
                              {safe(desc, isAr ? 'لا يوجد وصف' : 'No description')}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : !loading ? (
                  <tr>
                    <td colSpan={7} className='text-center py-5'>
                      <div className='text-muted'>
                        <i className='fas fa-chart-bar fa-3x mb-3 opacity-50'></i>
                        <h6>{t('NoData')}</h6>
                        <small>{t('TryChangingFilters')}</small>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </Loading>

        <CardFooter>
          <div className='d-flex justify-content-between align-items-center flex-wrap gap-3'>
            <ReportPagination reportKey={REPORT_KEY} maxVisiblePages={3} showItemsPerPage={true} />
          </div>
        </CardFooter>
      </Card>

      <ReportExportExcelModal
        show={showExport}
        onHide={() => setShowExport(false)}
        reportKey={REPORT_KEY}
        endpoint={ENDPOINT}
        columns={exportColumns}
        exportPageSize={4000}
        sheetName={({ isAr }) => (isAr ? 'كشف حساب جهة تحليلية' : 'Analytic Party Statement')}
        fileName={({ reportKey }) => `${reportKey}_${new Date().toISOString().slice(0, 10)}.xlsx`}
      />
    </>
  );
};

export default AnalyticPartyStatement_Paged;
